import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { useViewport } from '../../hooks/useViewport';
import { useFloorBounds } from '../../hooks/useFloorBounds';
import { getMinimapTransform, minimapToWorld } from './MinimapUtils';
import { renderMinimap } from './MinimapRenderer';
import { ViewportRect } from './ViewportRect';
import { LayoutElement } from '../../types';
import { useSimulationDxfRaster } from '../../hooks/useSimulationDxfRaster';

interface CanvasMinimapProps {
  objects?: Record<string, LayoutElement>;
  zoom?: number;
  panX?: number;
  panY?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  onNavigate?: (worldX: number, worldY: number) => void;
}

export const CanvasMinimap: React.FC<CanvasMinimapProps> = ({
  objects,
  zoom,
  panX,
  panY,
  viewportWidth,
  viewportHeight,
  onNavigate,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hook subscriptions
  const [storeViewport, setStoreViewport] = useViewport();
  const bounds = useFloorBounds();

  // Redux state
  const elements = useAppSelector((state) => state.layout.elements);
  const elementOrder = useAppSelector((state) => state.layout.elementOrder);
  const polylines = useAppSelector((state) => state.floorplan.polylines);
  const polylineLayers = useAppSelector((state) => state.floorplan.polylineLayers);
  const layers = useAppSelector((state) => state.floorplan.layers);
  const darkMode = useAppSelector((state) => state.ui.darkMode);
  const floorRaster = useSimulationDxfRaster();

  // Resolve values (props take precedence over centralized store state)
  const activeViewport = {
    zoom: zoom ?? storeViewport.zoom,
    panX: panX ?? storeViewport.panX,
    panY: panY ?? storeViewport.panY,
    canvasWidth: viewportWidth ?? storeViewport.canvasWidth,
    canvasHeight: viewportHeight ?? storeViewport.canvasHeight,
  };
  const activeElements = objects ?? elements;

  // Responsive mobile check
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contentRef = useRef<HTMLDivElement>(null);
  const [minimapSize, setMinimapSize] = useState({ width: 280, height: 170 });

  // Update canvas size to match actual measured container size (prevents aspect ratio stretching mismatches)
  useEffect(() => {
    if (collapsed) return;
    const updateSize = () => {
      if (contentRef.current) {
        const w = contentRef.current.clientWidth;
        const h = contentRef.current.clientHeight;
        if (w > 0 && h > 0) {
          setMinimapSize({ width: w, height: h });
        }
      }
    };
    
    updateSize();
    const observer = new ResizeObserver(updateSize);
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }
    return () => observer.disconnect();
  }, [collapsed]);

  // Minimap dimensions (fixed container sizing matching desktop and responsive mobile floating styles)
  const height = collapsed ? (isMobile ? 48 : 32) : isMobile ? 210 : 140;
  const canvasWidth = Math.max(1, minimapSize.width);
  const canvasHeight = Math.max(1, minimapSize.height);

  // Get current transform mapping (scale, offset) based on floor plan bounds
  const transform = getMinimapTransform(bounds, canvasWidth, canvasHeight);

  // Re-draw when dependencies change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || collapsed || canvasWidth <= 0 || canvasHeight <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId = requestAnimationFrame(() => {
      renderMinimap({
        ctx,
        bounds,
        transform,
        polylines,
        polylineLayers,
        layers,
        elements: activeElements,
        elementOrder,
        darkMode,
        rasterTiles: floorRaster.ready ? floorRaster.overviewTiles : undefined,
      });
    });

    return () => {
      cancelAnimationFrame(animId);
    };
  }, [
    bounds,
    transform,
    polylines,
    polylineLayers,
    layers,
    activeElements,
    elementOrder,
    darkMode,
    floorRaster.ready,
    floorRaster.overviewTiles,
    collapsed,
    canvasWidth,
    canvasHeight,
  ]);

  // Click-to-Navigate handler
  const handleMinimapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (collapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert click position to world coordinates
    const worldPt = minimapToWorld(clickX, clickY, bounds, transform);

    if (onNavigate) {
      onNavigate(worldPt.x, worldPt.y);
    } else {
      // Center the viewport on click position
      const targetPanX = activeViewport.canvasWidth / 2 - worldPt.x * activeViewport.zoom;
      const targetPanY = activeViewport.canvasHeight / 2 - worldPt.y * activeViewport.zoom;

      setStoreViewport({
        panX: targetPanX,
        panY: targetPanY,
      });
    }
  };

  const handlePan = useCallback(
    (newPanX: number, newPanY: number) => {
      setStoreViewport({
        panX: newPanX,
        panY: newPanY,
      });
    },
    [setStoreViewport]
  );

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsed(!collapsed);
  };

  const formattedZoom = Math.round(activeViewport.zoom * 100);

  // Mobile Floating Collapse design
  if (isMobile && collapsed) {
    return (
      <button
        onClick={toggleCollapse}
        className="fixed bottom-4 right-4 z-40 flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg border border-blue-500/20 cursor-pointer transition-all duration-300"
        title="Open Minimap"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L16 4m0 13V4m0 0L9 7" />
        </svg>
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`select-none flex flex-col overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 ${
        isMobile
          ? 'fixed bottom-4 right-4 z-40 rounded-2xl w-[300px]'
          : 'w-full border-0'
      } transition-all duration-300`}
      style={{ height: `${height}px` }}
    >
      {/* Header bar */}
      <div
        onClick={isMobile ? undefined : toggleCollapse}
        className="flex items-center justify-between px-2.5 py-1 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-900/50 transition-colors"
      >
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd" />
          </svg>
          Navigator
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium bg-slate-200/55 dark:bg-slate-800 px-1.5 py-0.5 rounded">
            {formattedZoom}%
          </span>
          <button
            onClick={toggleCollapse}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded cursor-pointer transition-colors"
            title={collapsed ? 'Expand Navigator' : 'Collapse Navigator'}
          >
            {collapsed ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Map Content Area */}
      {!collapsed && (
        <div
          ref={contentRef}
          className="relative w-full overflow-hidden bg-slate-50 dark:bg-slate-950 flex-1"
          style={{ height: '108px' }}
          onClick={handleMinimapClick}
        >
          {/* Canvas for layout rendering */}
          <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
            className="absolute top-0 left-0 pointer-events-none"
          />

          {/* Draggable Viewport Rect overlay */}
          <ViewportRect
            viewport={activeViewport}
            bounds={bounds}
            transform={transform}
            onPan={handlePan}
          />
        </div>
      )}
    </div>
  );
};
export default CanvasMinimap;
